import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { CoinIcon, RubyIcon } from "@/components/Icons";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BottomNav from "@/components/BottomNav";
import EarningsTab from "@/components/EarningsTab";

const COIN_IMGS = ["/assets/coin-single.png", "/assets/coin-stack-small.png", "/assets/coin-stack-medium.png", "/assets/coin-stack-large.png", "/assets/coin-pile-huge.png"];
const UPI_OPTIONS = [
  { name: "GPay", logo: <svg width="14" height="14" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34.2 6 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34.2 6 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41.4 34.7 44 29.8 44 24c0-1.3-.1-2.6-.4-3.9z" /></svg> },
  { name: "PhonePe", logo: <span className="w-3.5 h-3.5 rounded-full bg-[#5F259F] text-white text-[8px] font-extrabold flex items-center justify-center">पे</span> },
  { name: "Paytm", logo: <span className="text-[9px] font-extrabold italic"><span className="text-[#002E6E]" style={{ color: "#00BAF2" }}>pay</span><span className="text-white">tm</span></span> },
  { name: "UPI", logo: <span className="text-[9px] font-extrabold italic text-white">UPI<span className="text-[#FF9933]">›</span></span> },
];

function useCountdown() {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const end = new Date(now); end.setHours(23, 59, 59, 999);
      let s = Math.max(0, Math.floor((end - now) / 1000));
      const h = String(Math.floor(s / 3600)).padStart(2, "0");
      const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
      const sec = String(s % 60).padStart(2, "0");
      setLeft(`${h}:${m}:${sec}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);
  return left;
}

export default function Wallet() {
  const [packages, setPackages] = useState(null);
  const [firstEligible, setFirstEligible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [txns, setTxns] = useState([]);
  const [buying, setBuying] = useState(false);
  const [tab, setTab] = useState("coins");
  const [upi, setUpi] = useState("GPay");
  const [effects, setEffects] = useState(null);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const countdown = useCountdown();

  const loadEffects = () => api.get("/store/entry-effects").then(({ data }) => setEffects(data.effects)).catch(() => setEffects([]));

  useEffect(() => {
    api.get("/wallet/packages").then(({ data }) => {
      setPackages(data.packages);
      setFirstEligible(Boolean(data.first_recharge_eligible));
      const best = data.packages.find((p) => p.popular) || data.packages[0];
      setSelected(best?.id || null);
    }).catch(() => setPackages([]));
    api.get("/wallet/transactions").then(({ data }) => setTxns(data.transactions)).catch(() => { });
    loadEffects();
  }, []);

  const buyEffect = async (eff) => {
    try {
      const { data } = await api.post(`/store/entry-effects/${eff.id}/buy`);
      setUser((u) => ({ ...u, coins: data.coins, equipped_entry_effect: data.equipped }));
      toast.success(`${eff.name} unlocked & equipped! ${eff.emoji}`);
      loadEffects();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const equipEffect = async (eff) => {
    try {
      const { data } = await api.post(`/store/entry-effects/${eff.id}/equip`);
      setUser((u) => ({ ...u, equipped_entry_effect: data.equipped }));
      toast.success(data.equipped ? `${eff.name} equipped ${eff.emoji}` : "Effect unequipped");
      loadEffects();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const recharge = async () => {
    const pkg = packages?.find((p) => p.id === selected);
    if (!pkg) return toast.error("Select a coin pack first");
    setBuying(true);
    try {
      const { data } = await api.post("/payments/orders", { package_id: pkg.id });
      const { load } = await import("@cashfreepayments/cashfree-js");
      const cashfree = await load({ mode: data.mode });
      await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
    } catch (e) {
      toast.error(errText(e));
      setBuying(false);
    }
  };

  const txnLabel = { purchase: "Coin purchase", gift_send: "Gift sent", diamond_credit: "Rubies earned", admin_adjustment: "Admin adjustment", store_purchase: "Entry effect", call_charge: "Call payment", call_earning: "Call earnings", fan_join: "Fan club joined", fan_earning: "Fan club earnings", vip_subscription: "VIP membership" };

  const premiumIdx = packages ? packages.length - 1 : -1;
  const selectedPkg = packages?.find((p) => p.id === selected);

  return (
    <div className="min-h-screen max-w-md mx-auto px-4 py-6 pb-32">
      <div className="flex items-center gap-3 mb-5">
        <button data-testid="wallet-back" onClick={() => navigate("/")} className="glass-chip w-9 h-9 flex items-center justify-center text-white/80"><ArrowLeft size={16} /></button>
        <h1 className="text-white font-bold text-[28px] leading-none">Wallet</h1>
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="gradient-border rounded-3xl px-5 py-5 mb-6 flex items-center" data-testid="wallet-balance-card">
        <div className="flex-1 flex items-center justify-center gap-2.5" data-testid="wallet-coin-balance">
          <img src="/assets/coin-single.png" alt="coins" className="w-10 h-10 object-contain drop-shadow-[0_2px_10px_rgba(255,200,87,0.5)]" />
          <div>
            <p className="text-white text-2xl font-extrabold leading-none">{user?.coins?.toLocaleString()}</p>
            <p className="text-[#FFC857] text-xs font-bold mt-0.5">Coins</p>
          </div>
        </div>
        <span className="w-px h-10 bg-white/10" />
        <div className="flex-1 flex items-center justify-center gap-2.5" data-testid="wallet-diamond-balance">
          <img src="/assets/ruby-gem.png" alt="rubies" className="w-9 h-9 object-contain drop-shadow-[0_2px_10px_rgba(0,224,199,0.4)]" style={{ filter: "hue-rotate(168deg) saturate(1.15)" }} />
          <div>
            <p className="text-[#00E0C7] text-2xl font-extrabold leading-none">{user?.diamonds?.toLocaleString()}</p>
            <p className="text-[#00E0C7]/70 text-xs font-bold mt-0.5">Rubies</p>
          </div>
        </div>
      </motion.div>

      <div className="flex bg-[#14101F] rounded-full p-1 mb-6 border border-white/[0.06]">
        {[["coins", "Recharge"], ["store", "Effects"], ["earnings", "Earnings"]].map(([k, label]) => (
          <button key={k} data-testid={`wallet-tab-${k}`} onClick={() => setTab(k)}
            className={`flex-1 py-2.5 rounded-full text-xs font-bold transition-colors ${tab === k ? "bg-[#1E1830] text-white shadow-[0_2px_10px_rgba(0,0,0,0.4)]" : "text-[#6E6683]"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "earnings" && <EarningsTab />}

      {tab === "store" && (
        <div data-testid="effects-store">
          <h2 className="text-white text-lg font-bold mb-1">Entry Effects <span className="text-[#FFD86B]">✦</span></h2>
          <p className="text-[10px] text-[#6E6683] mb-3">Make an entrance! Your effect plays whenever you join a live room.</p>
          <div className="space-y-2.5">
            {effects === null && Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton h-16 rounded-2xl" />)}
            {effects?.map((e) => (
              <div key={e.id} data-testid={`effect-${e.id}`}
                className={`bg-[#1E1830] border rounded-2xl px-4 py-3.5 flex items-center justify-between ${e.equipped ? "border-[#FFD86B]/50" : "border-white/[0.06]"}`}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{e.emoji}</span>
                  <div>
                    <p className="text-white font-bold text-sm">{e.name}</p>
                    <p className="text-[#FFC857] text-[10px] font-semibold flex items-center gap-1"><CoinIcon size={10} /> {e.price.toLocaleString()}</p>
                  </div>
                </div>
                {e.owned ? (
                  <button data-testid={`effect-equip-${e.id}`} onClick={() => equipEffect(e)}
                    className={`text-xs font-bold px-4 py-1.5 rounded-full ${e.equipped ? "gradient-primary text-white" : "bg-white/10 text-[#B8B0C9]"}`}>
                    {e.equipped ? "Equipped ✓" : "Equip"}
                  </button>
                ) : (
                  <button data-testid={`effect-buy-${e.id}`} onClick={() => buyEffect(e)}
                    className="gradient-primary text-white text-xs font-bold px-4 py-1.5 rounded-full active:scale-95 transition-transform">
                    Buy
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "coins" && (<>
        <div className="grid grid-cols-3 gap-2.5 mb-4">
          {packages === null && Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-36 rounded-[20px]" />)}
          {packages?.map((p, i) => (
            <button key={p.id} data-testid={`package-${p.id}`} onClick={() => setSelected(p.id)}
              className={`relative overflow-hidden rounded-[20px] px-2 py-3 text-center transition-all active:scale-[0.97] ${i === premiumIdx
                ? "gradient-border"
                : selected === p.id
                  ? "bg-[#14101F] border border-[#FFD86B]/60 shadow-[0_0_16px_rgba(255,216,107,0.15)]"
                  : "bg-[#14101F] border border-white/[0.06]"
                }`}>
              {p.popular && (
                <span className="absolute top-3 -right-8 rotate-45 bg-[#FFD86B] text-[#0B0B14] text-[8px] font-extrabold px-8 py-0.5 shadow z-10">Best Value</span>
              )}
              {i === premiumIdx && <span className="absolute top-1 right-1.5 text-[11px]">👑</span>}
              <img src={COIN_IMGS[Math.min(i, COIN_IMGS.length - 1)]} alt="coins"
                className="w-12 h-12 object-contain mx-auto mb-1 drop-shadow-[0_4px_10px_rgba(255,200,87,0.4)]" />
              <p className="text-white font-extrabold text-sm">₹{p.price_inr.toLocaleString()}</p>
              <p className="text-[#FFC857] font-bold text-xs mt-0.5">→ {p.coins.toLocaleString()}</p>
              <p className="text-[#6E6683] text-[9px] mt-0.5">+18% GST</p>
              {p.bonus_percent > 0 && (
                <p className="text-[#FFD86B] text-[10px] font-bold mt-0.5">+{p.bonus_percent}% bonus</p>
              )}
            </button>
          ))}
        </div>

        {firstEligible && (
          <div className="relative overflow-hidden rounded-2xl px-4 py-3 mb-4 text-center gold-hairline"
            style={{ background: "linear-gradient(120deg, rgba(255,216,107,0.10), rgba(255,46,151,0.08))" }} data-testid="first-recharge-banner">
            <p className="text-[#FFD86B] text-xs font-extrabold">🎉 First recharge offer: Get 2x coins!</p>
            <p className="text-[#B8B0C9] text-[10px] mt-0.5">Countdown: <span className="text-white font-bold tabular-nums">{countdown}</span></p>
          </div>
        )}

        <div className="flex gap-2 mb-4" data-testid="upi-options">
          {UPI_OPTIONS.map((u) => (
            <button key={u.name} data-testid={`upi-${u.name.toLowerCase()}`} onClick={() => setUpi(u.name)}
              className={`relative flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-2xl text-[10px] font-bold transition-colors ${upi === u.name ? "bg-[#1E1830] border border-[#A03CF5]/60 text-white" : "bg-[#14101F] border border-white/[0.06] text-[#B8B0C9]"}`}>
              {u.logo} {u.name !== "Paytm" && u.name !== "UPI" ? u.name : ""}
              {upi === u.name && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#A03CF5] flex items-center justify-center"><Check size={9} className="text-white" /></span>
              )}
            </button>
          ))}
        </div>

        {selectedPkg && (
          <div className="bg-[#14101F] border border-white/[0.08] rounded-2xl p-3.5 mb-4 text-xs" data-testid="gst-breakdown-card">
            <div className="flex justify-between text-[#B8B0C9] mb-1">
              <span>Base Price:</span>
              <span>₹{selectedPkg.price_inr}</span>
            </div>
            <div className="flex justify-between text-[#B8B0C9] mb-1">
              <span>GST (18%):</span>
              <span className="text-[#FFD86B]">+₹{(selectedPkg.gst_amount || selectedPkg.price_inr * 0.18).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-white font-extrabold text-sm pt-2 border-t border-white/[0.08] mt-1">
              <span>Total Payable Amount:</span>
              <span className="text-[#FFC857]">₹{(selectedPkg.total_price_inr || selectedPkg.price_inr * 1.18).toFixed(2)}</span>
            </div>
          </div>
        )}

        <button data-testid="recharge-now-button" onClick={recharge} disabled={buying}
          className="btn-gradient w-full py-4 text-base active:scale-[0.98] transition-transform disabled:opacity-60">
          {buying ? "Processing…" : `Pay ₹${(selectedPkg?.total_price_inr || (selectedPkg ? selectedPkg.price_inr * 1.18 : 0)).toFixed(2)} Now`}
        </button>

        <button data-testid="vip-subscribe-card" onClick={() => navigate("/vip")}
          className="w-full rounded-2xl px-4 py-3.5 mt-4 flex items-center justify-between active:scale-[0.98] transition-transform"
          style={{ background: "rgba(245,166,35,0.15)", border: "1px solid rgba(245,166,35,0.4)" }}>
          <p className="text-left text-[#F5A623] font-bold text-xs">👑 VIP Membership — <span className="text-white/90">+10% bonus coins · from ₹299/mo</span></p>
          <span className="bg-[#FFD86B] text-[#0B0B14] text-[10px] font-extrabold px-3 py-1.5 rounded-full shrink-0 ml-2">
            {user?.is_vip ? `${(user.vip_plan || "VIP").toUpperCase()}` : "Upgrade"}
          </span>
        </button>
        <p className="text-[11px] text-[#6E6683] mt-3 mb-6 text-center">Secure payments · Cashfree</p>

        <h2 className="text-white text-lg font-bold mb-3">Recent Transactions</h2>
        <div className="space-y-1.5" data-testid="transactions-list">
          {txns.length === 0 && <p className="text-[#6E6683] text-xs text-center py-4">No transactions yet</p>}
          {txns.slice(0, 15).map((t) => (
            <div key={t.id} className="flex items-center justify-between bg-[#14101F] border border-white/[0.06] rounded-2xl px-3.5 py-2.5">
              <div>
                <p className="text-xs text-white font-semibold">{txnLabel[t.type] || t.type}</p>
                <p className="text-[9px] text-[#6E6683]">{t.meta?.gift ? `${t.meta.gift}${t.meta.combo > 1 ? ` x${t.meta.combo}` : ""}` : new Date(t.created_at).toLocaleString()}</p>
              </div>
              <span className={`text-xs font-bold ${t.amount >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {t.amount >= 0 ? "+" : ""}{t.amount.toLocaleString()} {t.currency === "diamonds" ? <RubyIcon size={12} className="align-[-2px]" /> : <CoinIcon size={12} className="align-[-2px]" />}
              </span>
            </div>
          ))}
        </div>
      </>)}
      <BottomNav />
    </div>
  );
}
